import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInDays,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  getMonth,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { now } from '@/utils/dates';
import type { Compare, Granularity } from '@/utils/statisticsQuery/types';

export type CustomDateRange = DateRange & {
  title: string;
  granularity: Granularity;
  window: number;
  periodSlug: string;
  compare?: Compare;
  periodCompare?: string;
};

const buildPeriodSelect = (): Record<string, CustomDateRange> => {
  const today = now();
  const lastWeekDay = addWeeks(today, -1);
  const startQuarter = startOfQuarter(today);
  const startYearDay = startOfYear(today);
  const prevYear = addYears(today, -1);
  const isSecondHalfYear = getMonth(today) >= 6;

  return {
    lastweek: {
      title: 'Senaste veckan',
      from: startOfWeek(lastWeekDay),
      to: endOfWeek(lastWeekDay),
      periodSlug: 'lastweek',
      granularity: 'week',
      window: 1,
    },
    last4weeks: {
      title: 'Senaste 4 veckorna',
      from: startOfWeek(addWeeks(today, -4)),
      to: endOfWeek(lastWeekDay),
      periodSlug: 'last4weeks',
      granularity: 'week',
      window: 4,
    },
    last30days: {
      title: 'Senaste 30 dagarna',
      from: addDays(today, -30),
      to: addDays(today, -1),
      periodSlug: 'last30days',
      granularity: 'day',
      window: 30,
    },
    lastmonth: {
      title: 'Senaste månad',
      from: startOfMonth(addMonths(today, -1)),
      to: endOfMonth(addMonths(today, -1)),
      periodSlug: 'lastmonth',
      granularity: 'month',
      window: 1,
    },
    lastquarter: {
      title: 'Senaste kvartal',
      from: startOfQuarter(addQuarters(today, -1)),
      to: endOfQuarter(addQuarters(today, -1)),
      periodSlug: 'lastquarter',
      granularity: 'quarter',
      window: 1,
    },
    lasthalfyear: {
      title: 'Senaste halvår',
      from: isSecondHalfYear ? startYearDay : addMonths(startOfYear(prevYear), 5),
      to: isSecondHalfYear ? addMonths(endOfMonth(startYearDay), 5) : endOfYear(prevYear),
      periodSlug: 'lasthalfyear',
      granularity: 'half_year',
      window: 1,
    },
    lastyear: {
      title: 'Senaste helår',
      from: startOfYear(prevYear),
      to: endOfYear(prevYear),
      periodSlug: 'lastyear',
      granularity: 'year',
      window: 1,
    },
    currentquarter: {
      title: 'Innevarande kvartal',
      from: startQuarter,
      to: addDays(today, -1),
      periodSlug: 'currentquarter',
      granularity: 'day',
      window: differenceInDays(today, startQuarter),
    },
    custom: {
      title: 'Eget datumspann',
      from: today,
      to: today,
      periodSlug: 'custom',
      granularity: null,
      window: 1,
    },
  };
};

export { buildPeriodSelect };
